import React, { useState, useEffect } from 'react';
import { analyticsApi } from '../../api';
import type { UserStatsRow } from '../../types/dashboard';

interface Props {
  initialData7d: UserStatsRow[] | null;
  initialData30d: UserStatsRow[] | null;
}

export default function UserStatsTable({ initialData7d, initialData30d }: Props) {
  const [period, setPeriod] = useState<7 | 30>(7);
  const [data7d, setData7d] = useState(initialData7d);
  const [data30d, setData30d] = useState(initialData30d);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData7d(initialData7d); }, [initialData7d]);
  useEffect(() => { setData30d(initialData30d); }, [initialData30d]);

  const rows = period === 7 ? data7d : data30d;

  const handleToggle = async (p: 7 | 30) => {
    setPeriod(p);
    const current = p === 7 ? data7d : data30d;
    if (!current) {
      setLoading(true);
      try {
        const result = await analyticsApi.getUserStats(p);
        if (p === 7) setData7d(result);
        else setData30d(result);
      } catch { /* ignore */ }
      setLoading(false);
    }
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={headingStyle}>User Stats</h3>
        <div style={{ display: 'flex', gap: 4 }}>
          {([7, 30] as const).map((p) => (
            <button
              key={p}
              onClick={() => handleToggle(p)}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: period === p ? '#6366f1' : '#f3f4f6',
                color: period === p ? '#fff' : '#374151',
              }}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
      ) : !rows || rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>No data</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key} style={thStyle}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.user} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={tdStyle}>{row.user}</td>
                  <td style={tdNumStyle}>{row.diagrams_opened}</td>
                  <td style={tdNumStyle}>{row.equipments_marked}</td>
                  <td style={tdNumStyle}>{row.nodes_marked}</td>
                  <td style={tdNumStyle}>{row.equip_instruments}</td>
                  <td style={tdNumStyle}>{row.node_instruments}</td>
                  <td style={tdNumStyle}>{row.template_uses}</td>
                  <td style={tdNumStyle}>{row.sam_uses}</td>
                  <td style={tdNumStyle}>{row.active_minutes.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const COLUMNS = [
  { key: 'user', label: 'User' },
  { key: 'diagrams_opened', label: 'Diagrams' },
  { key: 'equipments_marked', label: 'Equipment' },
  { key: 'nodes_marked', label: 'Nodes' },
  { key: 'equip_instruments', label: 'Equip. Instr.' },
  { key: 'node_instruments', label: 'Node Instr.' },
  { key: 'template_uses', label: 'Templates' },
  { key: 'sam_uses', label: 'SAM' },
  { key: 'active_minutes', label: 'Active Min' },
];

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
};

const headingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
  margin: 0,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: '#374151',
  fontWeight: 500,
};

const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
